import React, { useState, useMemo, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { 
  ChevronUpIcon, 
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

import ClaimRewardModal from '../modals/ClaimRewardModal';
import { useMutateClaimReward } from '../../hooks/mutations';
import clsxm from '../../utils/clsxm';
import useLocalStates from '../../stores/localStates';

const RewardsTable = ({ data }) => {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const { openClaimModal:isClaimModalOpen, setOpenClaimModal:setIsClaimModalOpen } = useLocalStates();
  
  // Claim mutation
  const {mutateAsync:claimMutation, isPending:isClaiming} = useMutateClaimReward();

  // Handle claim reward modal
  const handleClaimClick = useCallback((reward) => {
    setSelectedReward(reward);
    setIsClaimModalOpen(true);
  }, []);

  const handleClaimReward = async (gameId) => {
    try {
      await claimMutation({ gameId },{
        onSuccess: () => {
          alert('🎉 Reward claimed successfully!');
          setIsClaimModalOpen(false);
          setSelectedReward(null);
        },
      });
    } catch (error) {
      console.log({error})
      // alert(`❌ Failed to claim reward: ${error.message || 'Unknown error'}`);
      setIsClaimModalOpen(false);
      setSelectedReward(null);
    }
  };

  const handleCloseModal = () => {
    if (!isClaiming) {
      setIsClaimModalOpen(false);
      setSelectedReward(null);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: 'roomId',
      header: 'Room ID',
      cell: ({ getValue }) => (
        <span className="font-mono text-sm text-primary-600 dark:text-primary-400">
          {getValue() || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'score',
      header: 'Score',
      cell: ({ row }) => {
        const game = row.original;
        const score = game?.player?.score ?? 0;
        return (
          <div className="flex items-center">
            <span className="font-semibold text-gray-900 dark:text-white">
              {score}
            </span>
            <span className="ml-1 text-sm text-gray-500 dark:text-gray-400">
              PTS
            </span>
          </div>
        );
      },
      sortingFn: 'basic',
    },
    {
      accessorKey: 'result',
      header: 'Result',
      cell: ({ row }) => {
        const game = row.original;
        const isWinner = game.isWinner;
        const colors = {
          win: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
          loss: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
        };
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${isWinner ? colors.win : colors.loss}`}>
            {isWinner ? 'WIN' : 'LOSS'}
          </span>
        );
      },
    },
 
    {
      accessorKey: 'endTime',
      header: 'Date',
      cell: ({ getValue }) => {
        const date = getValue() ? new Date(getValue()) : new Date();
        return (
          <div className="text-sm">
            <div className="text-gray-900 dark:text-white">
              {date.toLocaleDateString()}
            </div>
            <div className="text-gray-500 dark:text-gray-400">
              {date.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        );
      },
      sortingFn: 'datetime',
    },
    {
      accessorKey: 'claimStatus',
      header: 'Claim Status',
      cell: ({ row }) => {
        const game = row.original;
        
        // Determine status based on game data
        let status = 'not_eligible';
        let displayText = 'Not Eligible';
        let isClickable = false;
        
        if (game.isWinner) {
          if (game.claimed && game.claimTransaction) {
            status = game.claimTransaction.status; // 'completed', 'pending', 'failed'
            displayText = game.claimTransaction.status === 'completed' ? 'Claimed' : 
                         game.claimTransaction.status === 'pending' ? 'Pending' : 'Failed';
          } else {
            status = 'unclaimed';
            displayText = 'Unclaimed';
            isClickable = true;
          }
        }
        
        const colors = {
          completed: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
          pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
          failed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
          unclaimed: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/30 transition-colors duration-200',
          not_eligible: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
        };
        
        return (
          <span 
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colors[status] || colors.not_eligible} ${isClickable ? 'cursor-pointer hover:p-3' : ''}`}
            onClick={isClickable ? () => handleClaimClick(game) : undefined}
            title={isClickable ? 'Click to claim reward' : undefined}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      accessorKey: 'winners',
      header: 'Winners',
      cell: ({ row }) => {
        const game = row.original;
        const winners = game.winners || [];
        
        if (winners.length === 0) {
          return <span className="text-gray-500 dark:text-gray-400 text-sm">No winners</span>;
        }
        
        return (
          <div className="text-sm">
            {winners.slice(0, 2).map((winner, index) => (
              <div key={index} className="text-gray-900 dark:text-white">
                {winner.username || winner.address?.slice(0, 6) + '...' + winner.address?.slice(-4)}
              </div>
            ))}
            {winners.length > 2 && (
              <div className="text-gray-500 dark:text-gray-400">
                +{winners.length - 2} more
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'claimAction',
      header: 'Claim',
      cell: ({ row }) => {
        const game = row.original;
        const isWinner = Boolean(game.isWinner);
        const isCompleted = game?.claimTransaction?.status === 'completed';
        const isPending = game?.claimTransaction?.status === 'pending';
        const canClaim = isWinner && !isCompleted && !isPending;

        return (
          <button
            type="button"
            // disabled={!canClaim || isClaiming}
            onClick={() => handleClaimClick(game) }
            // onClick={canClaim ? () => handleClaimClick(game) : undefined}
            className={clsxm(
              'px-3 py-1.5 rounded-md text-sm font-medium border transition-colors duration-200',
              canClaim
                ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600 cursor-not-allowed'
            )}
            title={
              canClaim
                ? 'Claim reward'
                : isCompleted
                ? 'Already claimed'
                : isPending
                ? 'Claim pending'
                : 'Not eligible'
            }
          >
            {isClaiming && selectedReward?.id === game.id ? 'Claiming…' : canClaim ? 'Claim' : isCompleted ? 'Claimed' : isPending ? 'Pending' : 'Not Eligible'}
          </button>
        );
      },
    },
  ], [handleClaimClick]);

  const filteredData = useMemo(() => {
    if (statusFilter === 'all') return data;
    
    return data.filter(item => {
      // Map our status filter to the game data structure
      if (statusFilter === 'completed') {
        return item.claimed && item.claimTransaction && item.claimTransaction.status === 'completed';
      }
      if (statusFilter === 'pending') {
        return item.claimed && item.claimTransaction && item.claimTransaction.status === 'pending';
      }
      if (statusFilter === 'failed') {
        return item.claimed && item.claimTransaction && item.claimTransaction.status === 'failed';
      }
      if (statusFilter === 'unclaimed') {
        return item.isWinner && (!item.claimed || !item.claimTransaction);
      }
      if (statusFilter === 'not_eligible') {
        return !item.isWinner;
      }
      return true;
    });
  }, [data, statusFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const exportToCSV = () => {
    const csvContent = [
      // Header
      columns.map(col => col.header).join(','),
      // Data rows
      ...table.getFilteredRowModel().rows.map(row =>
        row.getVisibleCells().map(cell => {
          const value = cell.getValue();
          if (value instanceof Date) {
            return value.toISOString();
          }
          return typeof value === 'string' ? `"${value}"` : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rewards-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={clsxm("bg-white dark:bg-gray-800  border border-gray-200 dark:border-gray-700")}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Rewards History
          </h3>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search rewards..."
                className="pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm appearance-none"
              >
                <option value="all">All Games</option>
                <option value="completed">Claimed</option>
                <option value="pending">Pending Claim</option>
                <option value="unclaimed">Unclaimed Wins</option>
                <option value="not_eligible">Losses</option>
                <option value="failed">Failed Claims</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <FunnelIcon className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Export Button */}
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition-colors duration-200"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center space-x-1">
                      <span>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      <span className="flex flex-col">
                        {header.column.getCanSort() && (
                          <>
                            <ChevronUpIcon
                              className={`h-3 w-3 ${
                                header.column.getIsSorted() === 'asc'
                                  ? 'text-primary-600'
                                  : 'text-gray-300'
                              }`}
                            />
                            <ChevronDownIcon
                              className={`h-3 w-3 -mt-1 ${
                                header.column.getIsSorted() === 'desc'
                                  ? 'text-primary-600'
                                  : 'text-gray-300'
                              }`}
                            />
                          </>
                        )}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {table.getRowModel().rows.map(row => (
              <tr 
                key={row.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{' '}
              of {table.getFilteredRowModel().rows.length} results
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: table.getPageCount() }, (_, i) => (
                <button
                  key={i}
                  onClick={() => table.setPageIndex(i)}
                  className={`px-3 py-1 rounded-lg text-sm transition-colors duration-200 ${
                    table.getState().pagination.pageIndex === i
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Claim Reward Modal */}
      <ClaimRewardModal
        isOpen={isClaimModalOpen}
        onClose={handleCloseModal}
        rewardData={selectedReward}
        onClaim={handleClaimReward}
        isLoading={isClaiming}
      />
    </div>
  );
};

export default RewardsTable;
